import React, { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import { DateRange } from "react-date-range";

export const DateFilter = ({ onDateChange }) => {
    const dateRef = useRef(null);
    const [showCustom, setShowCustom] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const dateButtonRef = useRef(null);
    const calendarRef = useRef(null);

    const [dateRange, setDateRange] = useState([
        {
            startDate: dayjs().startOf("day").toDate(),
            endDate: dayjs().endOf("day").toDate(),
            key: "selection",
        },
    ]);

    const [tempDateRange, setTempDateRange] = useState([
        {
            startDate: dayjs().startOf("day").toDate(),
            endDate: dayjs().endOf("day").toDate(),
            key: "selection",
        },
    ]);

    const dateOptions = [
        {
            label: "Today",
            range: () => ({
                startDate: dayjs().startOf("day").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "Yesterday",
            range: () => ({
                startDate: dayjs().subtract(1, "day").startOf("day").toDate(),
                endDate: dayjs().subtract(1, "day").endOf("day").toDate(),
            }),
        },
        {
            label: "Last 7 Days",
            range: () => ({
                startDate: dayjs().subtract(6, "day").startOf("day").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "Last 30 Days",
            range: () => ({
                startDate: dayjs().subtract(29, "day").startOf("day").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        {
            label: "Last Week",
            range: () => ({
                startDate: dayjs().subtract(1, "week").startOf("week").toDate(),
                endDate: dayjs().subtract(1, "week").endOf("week").toDate(),
            }),
        },
        {
            label: "Last Month",
            range: () => ({
                startDate: dayjs().subtract(1, "month").startOf("month").toDate(),
                endDate: dayjs().subtract(1, "month").endOf("month").toDate(),
            }),
        },
        {
            label: "This Year",
            range: () => ({
                startDate: dayjs().startOf("year").toDate(),
                endDate: dayjs().endOf("day").toDate(),
            }),
        },
        { label: "Custom", custom: true },
    ];

    // ✅ Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dateRef.current && !dateRef.current.contains(event.target)) {
                setShowDropdown(false);
                setShowCustom(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleDateOptionClick = (option) => {
        if (option.custom) {
            setTempDateRange([...dateRange]);
            setShowDropdown(false);
            requestAnimationFrame(() => setShowCustom(true));
        } else {
            const range = option.range();
            const newRange = [
                {
                    startDate: new Date(range.startDate),
                    endDate: new Date(range.endDate),
                    key: "selection",
                },
            ];
            setDateRange(newRange);
            setShowDropdown(false);
            setShowCustom(false);
            onDateChange && onDateChange(newRange); // ✅ pass to parent
        }
    };

    return (
        <div>
            <div className="relative sm:w-[200px] w-full" ref={dateRef}>
                {/* Button */}
                <button
                    className="w-full bg-white h-9 px-3 text-xs font-semibold border-2 rounded-lg focus:outline-none text-left flex items-center justify-between text-gray-400"
                    onClick={() => {
                        if (showCustom) {
                            setShowDropdown(false);
                            setShowCustom(false);
                        } else {
                            setShowDropdown((prev) => !prev);
                            setShowCustom(false);
                        }
                    }}
                    ref={dateButtonRef}
                >
                    <span>
                        {dateRange[0].startDate && dateRange[0].endDate
                            ? `${dayjs(dateRange[0].startDate).format("DD/MM/YYYY")} - ${dayjs(
                                dateRange[0].endDate
                            ).format("DD/MM/YYYY")}`
                            : "Select Date"}
                    </span>
                    <ChevronDown
                        className={`w-4 h-4 ml-2 transform transition-transform ${showDropdown ? "rotate-180" : ""
                            }`}
                    />
                </button>

                {/* Dropdown */}
                {showDropdown && (
                    <div className="absolute w-full bg-white border rounded shadow p-2 z-[50]">
                        <ul>
                            {dateOptions.map((option, idx) => (
                                <li
                                    key={idx}
                                    className="cursor-pointer hover:bg-purple-50 px-3 py-2 text-[12px] font-[600] text-gray-500"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleDateOptionClick(option);
                                    }}
                                >
                                    {option.label}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Custom Calendar */}
                {showCustom && (
                    <div
                        className="absolute top-[110%] left-0 bg-white border rounded shadow-lg p-3 z-[100] w-[260px]"
                        ref={calendarRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DateRange
                            editableDateInputs={true}
                            onChange={(item) => setTempDateRange([item.selection])}
                            ranges={tempDateRange}
                            moveRangeOnFirstSelection={false}
                            showMonthAndYearPickers={false}
                            rangeColors={["#632C9D"]}
                            months={1}
                            direction="horizontal"
                            showDateDisplay={false}
                            className="custom-date-range text-[12px] font-[600] text-gray-500"
                        />

                        <div className="flex justify-end gap-2 mt-2">
                            <button
                                className="bg-[#632C9D] text-white px-3 py-1 text-xs rounded"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDateRange(tempDateRange);
                                    setShowCustom(false);
                                    setShowDropdown(false);
                                    onDateChange && onDateChange(tempDateRange); // ✅ send to parent
                                }}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
